import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { GlobalWorkerOptions, version as pdfjsDistVersion } from 'pdfjs-dist';
import { DocumentType } from '../../types/message';
import { getFileAttachmentIcon } from '../../Data/IconData';

// Match the worker setup used by the full PDF viewer so react-pdf can render
// off the main thread. Safe to assign repeatedly.
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsDistVersion}/build/pdf.worker.min.mjs`;

interface FileCardThumbnailProps {
    type: DocumentType;
    /** Locally-extracted text content, when available (text/code files). */
    content?: string | null;
    /** Remote URL of the file — used to render a PDF first-page thumbnail. */
    url?: string;
}

/**
 * The visual center of a file card. Mirrors the iOS FileCard thumbnail:
 *   - PDFs render their first page as an image preview.
 *   - Text/code files render a miniature of their content, like a page.
 *   - Anything without a preview falls back to a type icon.
 */
export const FileCardThumbnail = ({ type, content, url }: FileCardThumbnailProps) => {
    const [pdfFailed, setPdfFailed] = useState(false);
    const [shouldRenderPdf, setShouldRenderPdf] = useState(false);
    const thumbnailRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (type !== 'pdf' || !url || pdfFailed) {
            setShouldRenderPdf(false);
            return;
        }

        const element = thumbnailRef.current;
        if (!element || typeof IntersectionObserver === 'undefined') {
            setShouldRenderPdf(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setShouldRenderPdf(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });

        observer.observe(element);
        return () => observer.disconnect();
    }, [pdfFailed, type, url]);

    if (type === 'pdf' && url && !pdfFailed) {
        return (
            <div ref={thumbnailRef} className="file-card-pdf" aria-hidden="true">
                {shouldRenderPdf ? (
                    <Document
                        file={url}
                        loading={null}
                        error={null}
                        onLoadError={() => setPdfFailed(true)}
                        onSourceError={() => setPdfFailed(true)}
                    >
                        <Page
                            pageNumber={1}
                            width={100}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={null}
                            error={null}
                        />
                    </Document>
                ) : getFileAttachmentIcon(type)}
            </div>
        );
    }

    if (content) {
        return (
            <pre className="file-card-preview" aria-hidden="true">
                {content.slice(0, 1500)}
            </pre>
        );
    }

    return (
        <div className="file-card-icon">
            {getFileAttachmentIcon(type)}
        </div>
    );
};
